import axios from "axios";
import get from "lodash/get";
import nanoid from "nanoid";

import { store } from "../store";
import { setAccessToken, setExpired } from "../store/actions/appConfig";
import { getAuthRefreshToken, getPrevDeviceId } from "../store/selectors/appConfig";

import { NotificationsManager } from "../components/Notification";

import { encrypt, decrypt } from "./crypto";
import { log } from "../utils/loggger";
import { formatJsonData } from "./formatJsonData";
import { IGNORE_ERROR_CODES, HANDLED_ERROR_CODES } from "../constants";
import {
  ErrorOccurredWhilePerforming,
  ReCaptchaError,
  FieldsValidationError
} from "./serverErrors";
import { endpoints } from "../constants/config";
import { deviceId } from "./deviceId";
import {
  PreAuthInstance as preAuthApiClient,
  PostAuthInstance as postAuthApiClient
} from "./stpAxiosConfig";

const SYM_KEY_HEADER = "x-sym-key";
const REQUEST_ID_HEADER = "x-request-id";
const TRANSACTION_ID_HEADER = "X-Trxn-Id";
const ENCRYPT_METHODS = ["post", "put"];
const DEVICE_ID_HEADER = "device-id";
const ENCRYPTION_ENABLE = process.env.REACT_APP_ENCRYPTION_ENABLE || "N";
const rsaPublicKey = process.env.REACT_APP_RSA_PUBLIC_KEY;

const encryptionEnabled = ENCRYPTION_ENABLE === "Y";

/**
 * Handles error message extraction and creates appropriate Error object
 * @param {string|Error|*} serverError - The server error
 * @param {string|Error|*} error - The error object
 * @returns {Error} - Returns an Error object with appropriate message
 */
/* istanbul ignore next */
const handleErrorMessage = (serverError, error) => {
  let errorMessage = null;
  if (typeof serverError === "string" || typeof error === "string") {
    errorMessage = serverError || error;
  } else if (error instanceof Error) {
    return error;
  } else {
    errorMessage = "Unknown error occurred";
  }
  return new Error(errorMessage);
};

const ERROR_CONTENT_TYPE = "text/html";
export const DEVICE_ID = deviceId;
export const uploadClient = axios.create({
  baseURL: process.env.REACT_APP_UPLOAD_PATH || "https://doc-uploader-sit.rakbanktst.ae/docuploader"
});

export const wcmClient = axios.create({
  baseURL: process.env.REACT_APP_WCM_PATH || "https://dxp-dev.rakbank.ae"
});

const excludedPaths = ["/oauth2/token", "/efr/transactions"];

/* istanbul ignore next */
const prepareHeaders = (config, apiClient) => {
  const headers = { ...config.headers };
  if (apiClient === preAuthApiClient) {
    delete headers["refresh-token"];
  }
  return headers;
};

/* istanbul ignore next */
[preAuthApiClient, postAuthApiClient].forEach(apiClient => {
  apiClient.interceptors.request.use(config => {
    const headers = prepareHeaders(config, apiClient);
    const state = store.getState();
    const previousDeviceId = getPrevDeviceId(state);
    let deviceId = previousDeviceId || DEVICE_ID;
    return {
      ...config,
      headers: {
        ...headers,
        "Cache-Control": "no-cache, no-store",
        Pragma: "no-cache",
        [REQUEST_ID_HEADER]: nanoid(),
        // crypto.randomUUID supported only on 'https' or 'localhost' URL
        [TRANSACTION_ID_HEADER]: crypto?.randomUUID?.(),
        [DEVICE_ID_HEADER]: deviceId
      }
    };
  });

  /* istanbul ignore next */
  apiClient.interceptors.request.use(config => {
    if (
      encryptionEnabled &&
      rsaPublicKey &&
      ENCRYPT_METHODS.includes(config.method.toLowerCase()) &&
      !excludedPaths.some(path => config.url.includes(path))
    ) {
      const headers = prepareHeaders(config, apiClient);
      const [encryptedPayload, encryptedSymKey, symKey] = encrypt(
        rsaPublicKey,
        JSON.stringify(config.data)
      );

      return {
        ...config,
        headers: {
          ...headers,
          "Cache-Control": "no-cache, no-store",
          "Content-Type": "application/json",
          [SYM_KEY_HEADER]: encryptedSymKey
        },
        data: encryptedPayload,
        symKey
      };
    }

    return config;
  });
});

/* istanbul ignore next */
[preAuthApiClient, postAuthApiClient, uploadClient].forEach(instance => {
  instance.interceptors.response.use(response => {
    const state = store.getState();
    const accessToken = response.headers.accesstoken || response.headers.AccessToken;
    let refreshToken = getAuthRefreshToken(state);
    if (instance === postAuthApiClient) {
      refreshToken = response.headers["refresh-token"] || "";
    }

    if (accessToken) {
      store.dispatch(setAccessToken(accessToken, refreshToken));
    }

    return response;
  });
});
/* istanbul ignore next */
//check if the response.data string is html for network blociking cases
const handleNetworkError = response => {
  if (response?.headers?.["content-type"]?.includes(ERROR_CONTENT_TYPE)) {
    const notificationOptions = { title: "Oops", message: "Something went wrong" };
    NotificationsManager.add(notificationOptions);
    throw new Error("Network Error");
  }
};

/* istanbul ignore next */
[preAuthApiClient, postAuthApiClient].forEach(apiClient => {
  apiClient.interceptors.response.use(
    response => {
      const { symKey } = response.config;

      handleNetworkError(response);
      if (symKey && response.data && typeof response.data === "string") {
        let payload;

        try {
          payload = decrypt(symKey, response.data).data;
        } catch (e) {
          log({ e, symKey, data: response.data });
        }

        try {
          if (payload) {
            return {
              ...response,
              data: JSON.parse(payload)
            };
          }
        } catch (e) {
          log({ e, payload });
        }
      }
      return response;
    },
    error => {
      const {
        data,
        status,
        config: { symKey, url, skipOops = false }
      } = error.response;

      let jsonData = data;

      if (symKey && data && typeof data === "string") {
        let payload;

        try {
          payload = decrypt(symKey, data).data;
        } catch (e) {
          log({ e, symKey, data });
        }

        try {
          if (payload) {
            jsonData = JSON.parse(payload);
          }
        } catch (e) {
          log({ e, payload });
        }
      }

      let notificationOptions = {};
      let serverError = null;
      const ignoreNotificationUrls = [endpoints.createInviteUri];
      if (skipOops) {
        const errorToReject = handleErrorMessage(serverError, error);
        return Promise.reject(errorToReject);
      }
      if (jsonData) {
        const { errors, errorType } = jsonData;
        if (errorType === "InvalidCredentialsError") {
          serverError = new FieldsValidationError(jsonData);
          notificationOptions = {
            title: "Failed to login",
            message: "Invalid credentials"
          };
        } else if (errorType === "ReCaptchaError") {
          serverError = new ReCaptchaError(jsonData);
          notificationOptions = { title: "ReCaptchaError", message: errors };
        } else if (errorType === "FieldsValidation") {
          serverError = new FieldsValidationError(jsonData);
          notificationOptions = {
            title: "Validation Error On Server",
            message: get(jsonData, "errors[0].message", "Validation Error")
          };
        } else if (HANDLED_ERROR_CODES.includes(get(errors, "[0].errorCode"))) {
          serverError = new ErrorOccurredWhilePerforming(jsonData);
          notificationOptions = null;
        } else if (ignoreNotificationUrls.includes(url)) {
          // ro-assist-brd3-1
          notificationOptions = null;
        } else {
          log(jsonData);
          try {
            if (jsonData.status) {
              if (IGNORE_ERROR_CODES.includes(get(errors, "[0].errorCode"))) {
                notificationOptions = null;
              } else {
                const errorMessages = errors?.map(({ message }) => message);
                const debugNotificationOptions = formatJsonData(jsonData);

                notificationOptions = {
                  message: errorMessages.join(", "),
                  ...debugNotificationOptions
                };
              }
            }
          } catch (e) {
            log(e);
          }
        }
      } else if (status === 401) {
        store.dispatch(setExpired(true));
        notificationOptions = null;
      }

      if (notificationOptions && NotificationsManager.add) {
        NotificationsManager.add(notificationOptions);
      }
      const errorToReject = handleErrorMessage(serverError, error);
      return Promise.reject(errorToReject);
    }
  );
});
// remove code check for usage and revert
const kycTransactionAPIClient = {};

export const configureKYCTransactionAPIClient = (transactionId, kycAccessToken) => {
  kycTransactionAPIClient[transactionId] = axios.create({
    baseURL: process.env.REACT_APP_RAKBANK_KYC_API_PATH,
    headers: {
      Authorization: `Bearer ${kycAccessToken}`
    },
    timeout: 120000 // 120 seconds
  });
  return kycTransactionAPIClient[transactionId];
};
// remove code check for usage and revert

const apiClient = {
  preAuth: preAuthApiClient,
  postAuth: postAuthApiClient
};

export default apiClient;
